"use client";

import * as React from "react";
import {
  Box,
  Flex,
  Grid,
  Separator,
  Table,
  Tooltip,
  Text,
  type TextProps,
  VisuallyHidden,
} from "@radix-ui/themes";
import {
  Avatar,
  Badge,
  IconButton,
  Button,
  Select,
  Skeleton,
  TextField,
} from "./elements.js";
import { InviteUserDialog } from "./invite-user-dialog.js";
import { SearchProvider, useSearchContext } from "./search-provider.js";
import { useIsHydrated } from "./use-is-hydrated.js";
import { UserActionsDropdown } from "./user-actions-dropdown.js";
import { UsersFilter } from "./users-filter.js";
import { UsersSearch } from "./users-search.js";
import {
  getBestName,
  getComparativeReadableDate,
  getDomProps,
  getListMetadata,
  WidgetRootState,
  type WidgetRootDomProps,
} from "./utils.js";
import { USER_ROW_LIMIT } from "./constants.js";
import { useUsersManagementContext } from "./users-management-context.js";
import { Member, MemberRole, MembersQueryResult } from "../api/endpoint.js";
import { GenericError } from "./generic-error.js";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Translation } from "./i18n/translation.js";
import { useTranslation } from "./i18n/use-translation.js";
import { useLocale } from "./i18n/use-locale.js";

interface UsersManagementProps extends WidgetRootDomProps {
  rolesData: { roles: MemberRole[]; multipleRolesEnabled: boolean };
  userData: MembersQueryResult;
  disableRolesFilter?: boolean;
  // When the users list is loading new users
  isPending: boolean;
}

const UsersManagement: React.FC<UsersManagementProps> = ({
  userData,
  rolesData,
  isPending,
  disableRolesFilter,
  ...domProps
}) => {
  const users = userData?.data ?? [];
  const usersCount = users?.length ?? 0;
  const isHydrated = useIsHydrated();
  const pagination = getListMetadata(userData);

  const { dispatch } = useUsersManagementContext();
  const isMultipleRolesEnabled = rolesData.multipleRolesEnabled;
  const translate = useTranslation();

  // we only want to show the loading indicator for some buttons if the request
  // is still pending after 500ms. If the request is fast enough the indicator
  // is a bit jarring.
  const [deferredLoading, setDeferredLoading] = React.useState(false);
  React.useEffect(() => {
    if (isPending) {
      const timeoutId = window.setTimeout(() => {
        setDeferredLoading(true);
      }, 500);
      return () => {
        window.clearTimeout(timeoutId);
      };
    } else {
      setDeferredLoading(false);
    }
  }, [isPending]);

  const showPagination = !!(pagination.before || pagination.after);

  return (
    <SearchProvider>
      <Flex
        direction="column"
        gap="3"
        {...getWidgetRootDomProps("resolved", domProps)}
      >
        <Grid columns="1fr auto" gap="2">
          <Flex gap="2" align="center">
            <Box flexBasis="380px" flexGrow="0" flexShrink="1">
              <UsersSearch />
            </Box>
            <Box flexGrow="0" flexShrink="0">
              <UsersFilter
                roles={rolesData.roles}
                disabled={disableRolesFilter}
              />
            </Box>
          </Flex>
          <Box flexGrow="0" flexShrink="0" style={{ placeSelf: "flex-end" }}>
            <InviteUserDialog>
              <Button>
                <Translation
                  defaultMessage="Invite user"
                  id="NBXpRj"
                  description="Button text to invite a new user to the organization"
                />
              </Button>
            </InviteUserDialog>
          </Box>
        </Grid>
        <Table.Root variant="ghost" size="1">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell width="260px">
                <Translation
                  defaultMessage="User"
                  id="BBh86k"
                  description="Column header for user information in the users table"
                />
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="100px">
                {isMultipleRolesEnabled ? (
                  <Translation
                    defaultMessage="Roles"
                    id="A1DuWM"
                    description="Column header for user roles when multiple roles are enabled"
                  />
                ) : (
                  <Translation
                    defaultMessage="Role"
                    id="nQbe+Y"
                    description="Column header for user role when only single role is allowed"
                  />
                )}
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="140px">
                <Translation
                  defaultMessage="Last active"
                  id="oOwLGt"
                  description="Column header showing when users were last active"
                />
              </Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell width="28px" />
            </Table.Row>
          </Table.Header>

          <Table.Body
            style={{
              transition: `opacity 0.2s ease-out ${isPending ? "0.2s" : "0s"}`,
              opacity: isPending && usersCount > 0 ? 0.5 : 1,
            }}
          >
            {users.length > 0 ? (
              users.map((user) => {
                const userDisplayName = getBestName(user);
                const dimText =
                  user.status === "InviteRevoked" ||
                  user.status === "InviteExpired";
                return (
                  <Table.Row key={user.id} align="center">
                    <Table.RowHeaderCell>
                      <Flex
                        align="center"
                        gap="3"
                        overflow="hidden"
                        height="var(--space-7)"
                      >
                        <Avatar
                          size="2"
                          fallback={<FallbackUserIcon />}
                          src={user.profilePictureUrl ?? undefined}
                          dim={dimText}
                        />

                        {userDisplayName ? (
                          <Flex
                            direction="column"
                            align="start"
                            height="var(--space-7)"
                            justify="center"
                            overflow="hidden"
                          >
                            <Flex gap="2" align="center" minWidth="0">
                              <TableCellText dim={dimText}>
                                {userDisplayName}
                              </TableCellText>
                              <UserBadge user={user} />
                            </Flex>
                            <TableCellText
                              level="secondary"
                              title={user.email}
                              dim={dimText}
                            >
                              {user.email}
                            </TableCellText>
                          </Flex>
                        ) : (
                          <Flex gap="2" align="center" minWidth="0">
                            <TableCellText dim={dimText} title={user.email}>
                              {user.email}
                            </TableCellText>
                            <UserBadge user={user} />
                          </Flex>
                        )}
                      </Flex>
                    </Table.RowHeaderCell>
                    <Table.Cell>
                      <UserRolesCellContent
                        user={user}
                        isMultipleRolesEnabled={isMultipleRolesEnabled}
                        dimText={dimText}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <LastActive user={user} isHydrated={isHydrated} />
                    </Table.Cell>
                    <Table.Cell justify="end">
                      <UserActionsDropdown user={user}>
                        <IconButton
                          title={translate({
                            defaultMessage: "User actions",
                            id: "1nsmNS",
                            description:
                              "Button tooltip for opening user actions menu",
                          })}
                        >
                          <DotsHorizontalIcon />
                        </IconButton>
                      </UserActionsDropdown>
                    </Table.Cell>
                  </Table.Row>
                );
              })
            ) : (
              <Table.Row align="center">
                <Table.Cell colSpan={4}>
                  <UsersManagementEmptyState isPending={isPending} />
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>

        {showPagination ? (
          <Flex gap="2" justify="end">
            <Button
              variant="secondary"
              size="1"
              disabled={!pagination.after || isPending || undefined}
              loading={deferredLoading}
              onClick={() => {
                if (pagination.after) {
                  dispatch({
                    type: "SET_PAGINATION",
                    pagination: { after: pagination.after, before: null },
                  });
                }
              }}
            >
              <Translation
                defaultMessage="Previous"
                id="910Q6b"
                description="Button text to navigate to the previous page of users"
              />
            </Button>
            <Button
              variant="secondary"
              size="1"
              disabled={!pagination.before || isPending || undefined}
              loading={deferredLoading}
              onClick={() => {
                if (pagination.before) {
                  dispatch({
                    type: "SET_PAGINATION",
                    pagination: { before: pagination.before, after: null },
                  });
                }
              }}
            >
              <Translation
                defaultMessage="Next"
                id="UlAY08"
                description="Button text to navigate to the next page of users"
              />
            </Button>
          </Flex>
        ) : null}
      </Flex>
    </SearchProvider>
  );
};

interface UsersManagementLoadingProps extends WidgetRootDomProps {}

const UsersManagementLoading: React.FC<UsersManagementLoadingProps> = (
  props,
) => {
  const translate = useTranslation();
  const placeholderText = (
    <Translation
      defaultMessage="All"
      id="l/Do9/"
      description="Filter option to show all users"
    />
  );
  return (
    <Flex
      direction="column"
      gap="3"
      {...getWidgetRootDomProps("loading", props)}
    >
      <Grid columns="1fr auto" gap="2">
        <Flex gap="2" align="center">
          <Skeleton loading>
            <Box flexBasis="380px" flexGrow="0" flexShrink="1">
              <TextField
                disabled
                aria-label={translate({
                  defaultMessage: "User search",
                  id: "lzPOKv",
                  description: "Label for the user search input field",
                })}
              />
            </Box>
          </Skeleton>
          <Skeleton loading>
            <Box flexGrow="0" flexShrink="0">
              <Select.Root disabled value="all" onValueChange={() => void 0}>
                <Select.Trigger>{placeholderText}</Select.Trigger>
                <Select.Content>
                  <Select.Item value="all">{placeholderText}</Select.Item>
                </Select.Content>
              </Select.Root>
            </Box>
          </Skeleton>
        </Flex>
        <Skeleton loading>
          <Box flexGrow="0" flexShrink="0" style={{ placeSelf: "flex-end" }}>
            <Button disabled>
              <Translation
                defaultMessage="Invite user"
                id="NBXpRj"
                description="Button text to invite a new user to the organization"
              />
            </Button>
          </Box>
        </Skeleton>
      </Grid>
      <Table.Root variant="ghost" size="1">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeaderCell width="260px">
              <Skeleton loading>
                <Translation
                  defaultMessage="User"
                  id="BBh86k"
                  description="Column header for user information in the users table"
                />
              </Skeleton>
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell width="100px">
              <Skeleton>
                <Translation
                  defaultMessage="Role"
                  id="FK7mP4"
                  description="Column header for user role"
                />
              </Skeleton>
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell width="140px">
              <Skeleton>
                <Translation
                  defaultMessage="Last active"
                  id="oOwLGt"
                  description="Column header showing when users were last active"
                />
              </Skeleton>
            </Table.ColumnHeaderCell>
            <Table.ColumnHeaderCell width="28px" />
          </Table.Row>
        </Table.Header>

        <Table.Body>
          {Array.from({ length: USER_ROW_LIMIT }, (_, index) => (
            <Table.Row key={index} align="center">
              <Table.RowHeaderCell>
                <Flex align="center" gap="3">
                  <Skeleton>
                    <Avatar size="2" fallback="F" />
                  </Skeleton>

                  <Flex
                    direction="column"
                    height="var(--space-7)"
                    justify="center"
                  >
                    <Skeleton width="180px" height="var(--space-4)" />
                    <Skeleton width="90px" height="var(--space-3)" mt="1" />
                  </Flex>
                </Flex>
              </Table.RowHeaderCell>
              <Table.Cell>
                <Flex wrap="wrap" gap="1">
                  <Skeleton width="75px" height="var(--space-4)" />
                </Flex>
              </Table.Cell>
              <Table.Cell>
                <Skeleton width="120px" height="var(--space-4)" />
              </Table.Cell>
              <Table.Cell justify="end" />
            </Table.Row>
          ))}
        </Table.Body>
      </Table.Root>

      <Flex gap="2" justify="end">
        <Skeleton loading>
          <Button disabled variant="secondary" size="1">
            <Translation
              defaultMessage="Previous"
              id="910Q6b"
              description="Button text to navigate to the previous page of users"
            />
          </Button>
        </Skeleton>
        <Skeleton loading>
          <Button disabled variant="secondary" size="1">
            <Translation
              defaultMessage="Next"
              id="UlAY08"
              description="Button text to navigate to the next page of users"
            />
          </Button>
        </Skeleton>
      </Flex>
    </Flex>
  );
};

interface UsersManagementErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const UsersManagementError: React.FC<UsersManagementErrorProps> = ({
  error,
  ...domProps
}) => {
  return (
    <GenericError
      error={error}
      minHeight="676px"
      {...getWidgetRootDomProps("error", domProps)}
    />
  );
};

function UserBadge({ user }: { user: Member }) {
  // TODO: This is not yet available in the data. Update here after API is updated.
  if (user.isLoggedInUser) {
    return (
      <Badge color="gray" style={{ userSelect: "none" }}>
        <Translation
          defaultMessage="You"
          id="ER1Vmv"
          description="Badge indicating the current logged-in user"
        />
      </Badge>
    );
  }
  if (user.status === "Invited") {
    return (
      <Badge color="amber" style={{ userSelect: "none" }}>
        <VisuallyHidden>
          <Translation
            defaultMessage="Status: "
            id="E2o/+H"
            description="Prefix for status indicator in screen reader"
          />
        </VisuallyHidden>
        <Translation
          defaultMessage="Invited"
          id="byIkUu"
          description="Badge status indicating user has been invited but not yet accepted"
        />
      </Badge>
    );
  }
  if (user.status === "InviteExpired") {
    return (
      <Badge color="red" style={{ userSelect: "none" }}>
        <VisuallyHidden>
          <Translation
            defaultMessage="Status: Invite "
            id="BWvbwY"
            description="Prefix for invite status indicator in screen reader"
          />
        </VisuallyHidden>
        <Translation
          defaultMessage="Expired"
          id="+0sQaN"
          description="Badge status indicating user invitation has expired"
        />
      </Badge>
    );
  }
  if (user.status === "InviteRevoked") {
    return (
      <Badge color="red" style={{ userSelect: "none" }}>
        <VisuallyHidden>
          <Translation
            defaultMessage="Status: Invite "
            id="BWvbwY"
            description="Prefix for invite status indicator in screen reader"
          />
        </VisuallyHidden>
        <Translation
          defaultMessage="Revoked"
          id="NNBv+r"
          description="Badge status indicating user invitation has been revoked"
        />
      </Badge>
    );
  }
  return null;
}

interface LastActiveProps {
  user: Member;
  isHydrated: boolean;
  dim?: boolean;
}

function LastActive(props: LastActiveProps) {
  if (!props.user.lastActivityAt) {
    return (
      <>
        <VisuallyHidden>
          {props.user.status === "Active" ? (
            <Translation
              defaultMessage="Never"
              id="0duvF4"
              description="Screen reader text indicating user has never been active"
            />
          ) : (
            <Translation
              defaultMessage="Not active"
              id="hd2+kk"
              description="Screen reader text indicating user is not active"
            />
          )}
        </VisuallyHidden>
        <Separator />
      </>
    );
  }
  return <LastActiveImpl {...props} date={props.user.lastActivityAt} />;
}

function LastActiveImpl({
  date,
  isHydrated,
  dim,
}: LastActiveProps & { date: string }) {
  const locale = useLocale();
  const { lastActiveDateTime, lastActiveDisplay } = React.useMemo(() => {
    const defaultTimeZone = "America/Los_Angeles";
    const lastActiveDate = new Date(date);
    const lastActiveDateTime = lastActiveDate.toLocaleTimeString(locale, {
      // hard-coded timezone before hydration to prevent server/client mismatch
      timeZone: isHydrated ? undefined : defaultTimeZone,
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "numeric",
    });

    // Server and client may produce a different 'now' date, so only
    // show comparative date if the component is hydrated to prevent a
    // server/client mismatch
    const lastActiveDisplay = isHydrated
      ? getComparativeReadableDate(new Date(), lastActiveDate, { locale })
      : lastActiveDate.toLocaleDateString(locale, {
          // hard-coded timezone to prevent server/client mismatch
          timeZone: defaultTimeZone,
          month: "long",
          day: "numeric",
          year: "numeric",
        });

    return {
      lastActiveDateTime,
      lastActiveDisplay,
    };
  }, [isHydrated, date, locale]);

  // handle cases where the DB might return an invalid date string
  if (lastActiveDisplay === "Invalid Date") {
    return (
      <>
        <VisuallyHidden>
          <Translation
            defaultMessage="Unknown"
            id="UUigrK"
            description="Screen reader text indicating last active date is unknown"
          />
        </VisuallyHidden>
        <Separator />
      </>
    );
  }

  return (
    <TableCellText asChild dim={dim}>
      <time dateTime={date} title={lastActiveDateTime}>
        {lastActiveDisplay}
      </time>
    </TableCellText>
  );
}

const TableCellText = React.forwardRef<HTMLSpanElement, TableCellTextProps>(
  function TableCellText(
    { children, dim, level = "primary", ...props },
    forwardedRef,
  ) {
    return (
      <Text
        ref={forwardedRef}
        color={level === "secondary" ? "gray" : undefined}
        weight={level === "secondary" ? "regular" : "medium"}
        size={level === "secondary" ? "1" : "2"}
        truncate
        {...props}
        style={
          dim
            ? {
                // TODO: use CSS var instead of hard-coded value for opacity
                opacity: 0.6,
                ...props.style,
              }
            : props.style
        }
      >
        {children}
      </Text>
    );
  },
);

type TableCellTextProps = TextProps & {
  level?: "primary" | "secondary";
  dim?: boolean;
};

const FallbackUserIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    fill="currentColor"
    viewBox="0 0 256 256"
  >
    {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
    <title>User icon</title>
    <path d="M229.19,213c-15.81-27.32-40.63-46.49-69.47-54.62a70,70,0,1,0-63.44,0C67.44,166.5,42.62,185.67,26.81,213a6,6,0,1,0,10.38,6C56.4,185.81,90.34,166,128,166s71.6,19.81,90.81,53a6,6,0,1,0,10.38-6ZM70,96a58,58,0,1,1,58,58A58.07,58.07,0,0,1,70,96Z" />
  </svg>
);

const UsersManagementEmptyState = ({ isPending }: { isPending: boolean }) => {
  const { clearSearch } = useSearchContext();
  const {
    state: { searchQuery },
  } = useUsersManagementContext();

  // When the search query is cleared, the users query is re-fetched which sends
  // us into a pending state. When this happens we want to keep a snapshot of
  // the previous search query while the query is revalidated. We can use this
  // to keep the 'No users found for query' UI in place until re-fetching is
  // complete, otherwise the view flips to 'No users found' very quickly before
  // the full table is shown again.
  const [{ isClearing, lastSearchQuery }, setClearing] = React.useState({
    isClearing: false,
    lastSearchQuery: null as null | string,
  });
  const [wasPending, setWasPending] = React.useState(isPending);
  if (wasPending !== isPending) {
    setWasPending(isPending);
    if (!isPending) {
      setClearing((prev) =>
        prev.isClearing ? { isClearing: false, lastSearchQuery: null } : prev,
      );
    }
  }

  if (searchQuery || isClearing) {
    return (
      <Flex align="center" justify="center" py="8" direction="column" gap="2">
        <Text size="2">
          <Translation
            defaultMessage="No users found for query '{query}'"
            id="wrEZ8+"
            description="Message shown when no users match the search query"
            values={{
              query: isClearing ? lastSearchQuery : searchQuery,
            }}
          />
        </Text>

        <Button
          variant="secondary"
          size="1"
          onClick={() => {
            setClearing({ isClearing: true, lastSearchQuery: searchQuery });
            clearSearch();
          }}
          loading={isPending}
        >
          <Translation
            defaultMessage="Clear search"
            id="Xv3fVI"
            description="Button text to clear the current search query"
          />
        </Button>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" py="8" gap="2">
      <Text size="2">
        <Translation
          defaultMessage="No users found"
          id="MQ+LoA"
          description="Message shown when there are no users in the system"
        />
      </Text>
    </Flex>
  );
};

interface UserRolesCellContentProps {
  user: Member;
  isMultipleRolesEnabled: boolean;
  dimText: boolean;
}

function UserRolesCellContent({
  user,
  isMultipleRolesEnabled,
  dimText,
}: UserRolesCellContentProps) {
  if (!user.roles || user.roles.length === 0) {
    return (
      <>
        <VisuallyHidden>
          <Translation
            defaultMessage="No roles assigned"
            id="CVOVB0"
            description="Screen reader text indicating user has no roles assigned"
          />
        </VisuallyHidden>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <span aria-hidden style={{ userSelect: "none" }}>
          –
        </span>
      </>
    );
  }

  if (isMultipleRolesEnabled && user.roles.length > 1) {
    const roleNames = user.roles?.map((role) => role.name).join(", ");
    return (
      <Tooltip content={roleNames} maxWidth="250px">
        <TableCellText dim={dimText}>
          <Translation
            defaultMessage="{count} roles"
            id="lWAmRC"
            description="Shows the number of roles assigned to a user (plural)"
            values={{ count: user.roles.length }}
          />
        </TableCellText>
      </Tooltip>
    );
  }

  return <TableCellText dim={dimText}>{user.roles?.[0]?.name}</TableCellText>;
}

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "users-management",
    widgetState: state,
  });
}

export type { UsersManagementLoadingProps, UsersManagementErrorProps };
export { UsersManagement, UsersManagementLoading, UsersManagementError };
